import { Fragment, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, IconButton, InputAdornment, Link, Stack, TextField, Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';

import { TEMAS, PREGUNTAS } from './preguntasFrecuentes';

// Preguntas frecuentes (/ayuda). El contenido vive en preguntasFrecuentes.js; aquí solo se muestra: buscador, temas y una
// pregunta por acordeón. Cada pregunta tiene su enlace directo (/ayuda#su-id) para mandarla desde otra pantalla o un mensaje.

const TODAS = 'todas';
const OTRAS = 'otras';
// Un tema nuevo sin ícono aquí usa el signo de interrogación
const ICONOS = {
  [TODAS]: AppsOutlinedIcon,
  empezar: RocketLaunchOutlinedIcon,
  contratar: EventAvailableOutlinedIcon,
  pagos: PaymentsOutlinedIcon,
  ofrecer: StorefrontOutlinedIcon,
  cuenta: LockOutlinedIcon,
  mensajes: ForumOutlinedIcon,
};
// Alto del encabezado fijo: lo que hay que dejar libre arriba al bajar hasta una pregunta o al pegar el menú de temas
const BAJO_ENCABEZADO = 110;

// Para comparar sin importar acentos ni mayúsculas ("Cancelación" = "cancelacion")
export const normalizar = (texto) => String(texto || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Enlace directo de una pregunta: sale de su texto ("¿Cómo contrato un servicio?" → "como-contrato-un-servicio")
export const idDePregunta = (pregunta) => normalizar(pregunta).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// Un enlace dentro de una respuesta: [lo que se lee](/ruta)
const ENLACE = /\[([^\]]+)\]\(([^)\s]+)\)/;
const PARTES = /(\[[^\]]+\]\([^)\s]+\))/;           // parte el texto en pedazos de texto y enlaces completos
const UN_ENLACE = new RegExp(`^${ENLACE.source}$`);
const ENLACES = new RegExp(ENLACE.source, 'g');

// Lo que se busca de una pregunta: ella misma y su respuesta, sin la marca de los enlaces
const textoDe = (p) => normalizar([p.pregunta, ...p.respuesta.flat()].join(' ').replace(ENLACES, '$1'));

// Palabras que no ayudan a encontrar nada: "¿cómo cancelo mi reserva?" busca "cancelo" y "reserva"
const VACIAS = new Set([
  'a', 'al', 'como', 'con', 'cual', 'cuando', 'de', 'del', 'donde', 'el', 'en', 'es', 'esta', 'hacer', 'hago', 'la', 'las', 'lo',
  'los', 'me', 'mi', 'mis', 'necesito', 'no', 'o', 'para', 'por', 'puedo', 'que', 'quiero', 'se', 'si', 'su', 'sus', 'te', 'tu',
  'tus', 'un', 'una', 'y', 'ya', 'yo',
]);
// Terminaciones que se le quitan a una palabra buscada (de la más larga a la más corta) para quedarse con su raíz: así
// "cancelación", "cancelaciones", "cancelar" y "cancelo" se encuentran entre sí, y "facturas" encuentra "factura". La raíz
// nunca queda de menos de 4 letras ("pin", "pago" y "oxxo" se buscan enteras).
const TERMINACIONES = [
  'aciones', 'iciones', 'ciones', 'acion', 'icion', 'mente', 'iendo', 'cion', 'ando', 'ados', 'adas', 'idos', 'idas',
  'ado', 'ada', 'ido', 'ida', 'ar', 'er', 'ir', 'es', 'a', 'e', 'o', 's',
];
export const raizDe = (palabra) => {
  const fin = TERMINACIONES.find((t) => palabra.endsWith(t) && palabra.length - t.length >= 4);
  return fin ? palabra.slice(0, -fin.length) : palabra;
};
export const palabrasDe = (busqueda) => {
  const todas = normalizar(busqueda).split(/[^a-z0-9]+/).filter(Boolean);
  const utiles = todas.filter((p) => !VACIAS.has(p));
  return (utiles.length ? utiles : todas).map(raizDe);
};

// Las preguntas que tienen todas las palabras buscadas, en la pregunta o en su respuesta. Si ninguna las tiene todas, las que
// tienen más de ellas, con `exactas: false` para avisarlo ("pido factura" no encuentra "pido", pero sí las de factura).
export const buscarPreguntas = (preguntas, busqueda) => {
  const palabras = palabrasDe(busqueda);
  const aciertos = preguntas.map((p) => { const texto = textoDe(p); return palabras.filter((w) => texto.includes(w)).length; });
  const conTodas = preguntas.filter((_, i) => aciertos[i] === palabras.length);
  if (conTodas.length > 0 || palabras.length < 2) return { preguntas: conTodas, exactas: true };
  const mejor = Math.max(0, ...aciertos);
  return { preguntas: mejor > 0 ? preguntas.filter((_, i) => aciertos[i] === mejor) : [], exactas: false };
};

// Una sección por tema, en el orden de TEMAS y solo con preguntas. Las de un tema que no existe van al final, en "Otras
// preguntas": así una errata en el tema no hace desaparecer la pregunta.
export const agruparPorTema = (preguntas, temas) => {
  const conocidos = new Set(temas.map((t) => t.id));
  return [...temas, { id: OTRAS, nombre: 'Otras preguntas' }]
    .map((t) => ({ ...t, preguntas: preguntas.filter((p) => (conocidos.has(p.tema) ? p.tema : OTRAS) === t.id) }))
    .filter((s) => s.preguntas.length > 0);
};

const LISTA = PREGUNTAS.map((p) => ({ ...p, id: idDePregunta(p.pregunta) }));
const MENU_TEMAS = [
  { id: TODAS, nombre: 'Todas las preguntas', corto: 'Todas', cuantas: LISTA.length },
  ...agruparPorTema(LISTA, TEMAS).map((s) => ({ id: s.id, nombre: s.nombre, cuantas: s.preguntas.length })),
];

const textoRespuesta = { fontSize: 15, lineHeight: 1.65, color: 'text.primary' };
const estiloEnlace = { color: 'primary.main', fontWeight: 600, textDecorationColor: (t) => alpha(t.palette.primary.main, 0.35) };
const estiloBoton = { textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5 };

// Texto con enlaces: las rutas de la web se abren aquí mismo y las de fuera en otra pestaña
const ConEnlaces = ({ texto }) => texto.split(PARTES).map((parte, i) => {
  const enlace = parte.match(UN_ENLACE);
  if (!enlace) return <Fragment key={i}>{parte}</Fragment>;
  const [, etiqueta, ruta] = enlace;
  return ruta.startsWith('/')
    ? <Link key={i} component={RouterLink} to={ruta} sx={estiloEnlace}>{etiqueta}</Link>
    : <Link key={i} href={ruta} target="_blank" rel="noopener noreferrer" sx={estiloEnlace}>{etiqueta}</Link>;
});

// Párrafos y viñetas de una respuesta
const Respuesta = ({ respuesta }) => respuesta.map((bloque, i) => (Array.isArray(bloque) ? (
  <Box key={i} component="ul" sx={{ m: 0, mb: 1.5, pl: 3, '& li::marker': { color: 'secondary.main' } }}>
    {bloque.map((punto, j) => (
      <Typography key={j} component="li" sx={{ ...textoRespuesta, mb: 0.75 }}><ConEnlaces texto={punto} /></Typography>
    ))}
  </Box>
) : (
  <Typography key={i} sx={{ ...textoRespuesta, mb: 1.5 }}><ConEnlaces texto={bloque} /></Typography>
)));

const Pregunta = ({ pregunta, abierta, alCambiar }) => (
  <Accordion
    id={pregunta.id}
    expanded={abierta}
    onChange={(_, abrir) => alCambiar(pregunta.id, abrir)}
    disableGutters
    elevation={0}
    sx={{
      mb: 1.5,
      border: '1px solid',
      borderColor: abierta ? 'secondary.main' : 'sutil',
      borderRadius: '12px',
      '&:first-of-type, &:last-of-type': { borderRadius: '12px' },
      '&::before': { display: 'none' },
      boxShadow: (t) => `0 1px 3px ${alpha(t.palette.primary.main, 0.08)}`,
      scrollMarginTop: `${BAJO_ENCABEZADO}px`,
      transition: 'border-color 0.2s',
    }}
  >
    {/* id y aria-controls: MUI los usa para ligar la respuesta (región) con su pregunta */}
    <AccordionSummary
      id={`${pregunta.id}-pregunta`}
      aria-controls={`${pregunta.id}-respuesta`}
      expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
      sx={{ px: { xs: 2, sm: 2.5 }, '& .MuiAccordionSummary-content': { my: 1.75 } }}
    >
      <Typography component="span" sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.4, color: 'primary.main' }}>
        {pregunta.pregunta}
      </Typography>
    </AccordionSummary>
    {/* Renglones de ~75 caracteres como máximo: en pantallas anchas se leen mejor que de orilla a orilla */}
    <AccordionDetails sx={{ px: { xs: 2, sm: 2.5 }, pt: 0, pb: 1, '& > *': { maxWidth: '75ch' } }}>
      <Respuesta respuesta={pregunta.respuesta} />
    </AccordionDetails>
  </Accordion>
);

// Temas: lista fija a la izquierda en pantallas grandes. En el celular y la tableta, una sola fila que se desliza de lado y llega a
// las orillas de la pantalla (en varias filas ocupaban hasta media pantalla antes de la primera pregunta).
const Temas = ({ elegido, alElegir }) => (
  <Box component="nav" aria-label="Temas de ayuda" sx={{ position: { md: 'sticky' }, top: { md: BAJO_ENCABEZADO } }}>
    <Stack
      direction={{ xs: 'row', md: 'column' }}
      spacing={1}
      useFlexGap
      sx={{
        flexWrap: 'nowrap',
        overflowX: { xs: 'auto', md: 'visible' },
        mx: { xs: -2, sm: -3, md: 0 },
        px: { xs: 2, sm: 3, md: 0 },
        pb: { xs: 0.5, md: 0 },
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {MENU_TEMAS.map(({ id, nombre, corto, cuantas }) => {
        const Icono = ICONOS[id] || HelpOutlineIcon;
        const activo = elegido === id;
        return (
          <Button
            key={id}
            onClick={() => alElegir(id)}
            aria-pressed={activo}
            startIcon={<Icono />}
            sx={{
              height: 'auto',
              minHeight: 40,
              flexShrink: 0,
              whiteSpace: { xs: 'nowrap', md: 'normal' },
              px: 1.75,
              justifyContent: 'flex-start',
              textAlign: 'left',
              textTransform: 'none',
              fontSize: 14,
              fontWeight: activo ? 700 : 500,
              borderRadius: { xs: '20px', md: '10px' },
              border: { xs: '1px solid', md: 'none' },
              borderColor: activo ? 'secondary.main' : 'sutil',
              color: activo ? 'primary.main' : 'text.secondary',
              bgcolor: (t) => (activo ? alpha(t.palette.secondary.main, 0.14) : 'transparent'),
              '&:hover': { bgcolor: (t) => (activo ? alpha(t.palette.secondary.main, 0.2) : t.palette.sutil) },
              '& .MuiButton-startIcon': { display: { xs: 'none', md: 'inherit' }, color: activo ? 'primary.main' : 'text.secondary' },
            }}
          >
            <Box component="span" sx={{ flex: { md: 1 } }}>
              {corto ? (
                <>
                  <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>{nombre}</Box>
                  <Box component="span" sx={{ display: { md: 'none' } }}>{corto}</Box>
                </>
              ) : nombre}
            </Box>
            <Box component="span" sx={{ display: { xs: 'none', md: 'inline' }, ml: 1, fontSize: 12, fontWeight: 600 }}>{cuantas}</Box>
          </Button>
        );
      })}
    </Stack>
  </Box>
);

const SinResultados = ({ busqueda, alLimpiar }) => (
  <Box sx={{ textAlign: 'center', py: 6, px: 2, borderRadius: '16px', border: '1px dashed', borderColor: 'secondary.main' }}>
    <SearchOffOutlinedIcon sx={{ fontSize: 44, color: 'secondary.main' }} />
    <Typography component="h2" sx={{ mt: 1.5, fontSize: 17, fontWeight: 700, color: 'primary.main', overflowWrap: 'anywhere' }}>
      No encontramos preguntas con «{busqueda.trim()}»
    </Typography>
    <Typography sx={{ mt: 0.5, fontSize: 15, color: 'text.secondary' }}>
      Prueba con otras palabras o escríbenos al chat de soporte.
    </Typography>
    <Button onClick={alLimpiar} sx={{ ...estiloBoton, mt: 2 }}>Ver todas las preguntas</Button>
  </Box>
);

const Contacto = () => (
  <Box
    component="aside"
    sx={{
      mt: 5, p: { xs: 2.5, sm: 3 }, borderRadius: '16px', bgcolor: 'sutil',
      display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2,
    }}
  >
    <SupportAgentOutlinedIcon sx={{ fontSize: 40, color: 'secondary.main', flexShrink: 0 }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700, color: 'primary.main' }}>¿No encontraste tu respuesta?</Typography>
      <Typography sx={{ mt: 0.5, fontSize: 15, color: 'text.secondary' }}>Escríbenos y nuestro equipo te ayuda.</Typography>
    </Box>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
      <Button component={RouterLink} to="/soporte" variant="contained" disableElevation sx={estiloBoton}>Ir al chat de soporte</Button>
      <Button component={RouterLink} to="/user/tickets" variant="outlined" sx={estiloBoton}>Ver mis tickets</Button>
    </Stack>
  </Box>
);

const Ayuda = () => {
  const { hash } = useLocation();
  const [busqueda, setBusqueda] = useState('');
  const [tema, setTema] = useState(TODAS);
  const [abiertas, setAbiertas] = useState([]);
  const preguntasRef = useRef(null);
  // En los celulares más angostos (320 px) el ejemplo no cabe en el buscador
  const conEjemplo = useMediaQuery('(min-width:360px)');

  // Enlace directo (/ayuda#id): abre esa pregunta y baja hasta ella. ScrollToTop sube la página al cambiar de ruta y lo repite en
  // el siguiente cuadro; por eso se baja dos cuadros después.
  useEffect(() => {
    let id = '';
    try { id = decodeURIComponent(hash.replace(/^#/, '')); } catch (e) { id = ''; }
    if (!LISTA.some((p) => p.id === id)) return undefined;
    setBusqueda('');
    setTema(TODAS);
    setAbiertas((actuales) => (actuales.includes(id) ? actuales : [...actuales, id]));
    let segundo;
    const primero = requestAnimationFrame(() => {
      segundo = requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }));
    });
    return () => { cancelAnimationFrame(primero); cancelAnimationFrame(segundo); };
  }, [hash]);

  const buscando = palabrasDe(busqueda).length > 0;
  const resultado = buscando ? buscarPreguntas(LISTA, busqueda) : { preguntas: LISTA, exactas: true };
  const secciones = agruparPorTema(resultado.preguntas, TEMAS).filter((s) => buscando || tema === TODAS || s.id === tema);
  const encontradas = resultado.preguntas.length;

  // Al cambiar de tema con la página ya abajo, vuelve al inicio de las preguntas (si no, quedaría a media lista o al final)
  const elegirTema = (id) => {
    setTema(id);
    setBusqueda('');
    const inicio = preguntasRef.current;
    if (inicio && inicio.getBoundingClientRect().top < BAJO_ENCABEZADO) inicio.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const alCambiar = (id, abrir) => setAbiertas((actuales) => (abrir ? [...actuales, id] : actuales.filter((x) => x !== id)));

  return (
    <Box className="main-page">
      <Box sx={{ bgcolor: 'secondary.main', color: 'primary.main', borderRadius: 2, px: 4, py: 5, mb: 3 }}>
        <Typography variant="h5" component="h1">Preguntas frecuentes</Typography>
        <Typography mt={2} variant="body1" color="primary">
          <small>Todo lo que necesitas saber para contratar y ofrecer servicios en OneService</small>
        </Typography>
      </Box>

      <TextField
        fullWidth
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder={conEjemplo ? 'Busca tu duda, por ejemplo: cancelar' : 'Busca tu duda'}
        slotProps={{
          htmlInput: { 'aria-label': 'Buscar en las preguntas frecuentes', enterKeyHint: 'search' },
          input: {
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'secondary.main' }} /></InputAdornment>,
            endAdornment: busqueda ? (
              <InputAdornment position="end">
                <IconButton edge="end" aria-label="Borrar la búsqueda" onClick={() => setBusqueda('')}><CloseIcon /></IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white', fontSize: 16 } }}
      />

      <Grid container spacing={{ xs: 2.5, md: 4 }} sx={{ mt: 2.5 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Temas elegido={buscando ? null : tema} alElegir={elegirTema} />
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <Box ref={preguntasRef} sx={{ scrollMarginTop: `${BAJO_ENCABEZADO}px` }}>
            {/* Se anuncia a los lectores de pantalla cada vez que cambia */}
            <Box aria-live="polite">
              {buscando && encontradas > 0 && (
                <Typography sx={{ mb: 2, fontSize: 15, color: 'text.secondary', overflowWrap: 'anywhere' }}>
                  {resultado.exactas
                    ? `${encontradas} ${encontradas === 1 ? 'pregunta' : 'preguntas'} con «${busqueda.trim()}»`
                    : `Ninguna pregunta tiene todas las palabras de «${busqueda.trim()}». Estas son las que más se parecen:`}
                </Typography>
              )}
            </Box>

            {secciones.length === 0 ? (
              <SinResultados busqueda={busqueda} alLimpiar={() => setBusqueda('')} />
            ) : secciones.map((seccion, i) => (
              <Box key={seccion.id} component="section" aria-labelledby={`tema-${seccion.id}`} sx={{ mt: i === 0 ? 0 : 4 }}>
                <Typography id={`tema-${seccion.id}`} component="h2" sx={{ mb: 1.5, fontSize: 19, fontWeight: 700, color: 'primary.main' }}>
                  {seccion.nombre}
                </Typography>
                {seccion.preguntas.map((p) => (
                  <Pregunta key={p.id} pregunta={p} abierta={abiertas.includes(p.id)} alCambiar={alCambiar} />
                ))}
              </Box>
            ))}

            <Contacto />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Ayuda;
